import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Pagination,
  Chip,
  InputAdornment
} from '@mui/material';
import { 
  Search,
  Refresh,
  FileDownload,
  CalendarToday,
  FilterList,
  Clear
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { WebSocketContext } from '../WebSocketContext';
import config from '../config';
import './MasterTrades.css';

const MasterTrades = () => {
  const { isDarkMode } = useTheme();
  const { userDetails } = useContext(WebSocketContext);
  
  // State for filters (matching mtrades.php structure)
  const [filters, setFilters] = useState({
    selectedClientId: '',
    selectedStockName: '',
    selectedBrokerId: '',
    selectedSegment: '',
    selectedStatus: '3', // Default to Executed (3)
    clientIP: '',
    orderBroker: ''
  });
  
  // State for data
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0);
  
  // State for dropdown options
  const [masters, setMasters] = useState([]);
  const [clients, setClients] = useState([]);
  const [orderTypes, setOrderTypes] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [brokers, setBrokers] = useState([]);
  
  // State for searchable dropdowns
  const [scriptSearchResults, setScriptSearchResults] = useState([]);
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [brokerSearchResults, setBrokerSearchResults] = useState([]);
  const [showScriptResults, setShowScriptResults] = useState(false);
  const [showClientResults, setShowClientResults] = useState(false);
  const [showBrokerResults, setShowBrokerResults] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Fetch orders data
  const fetchOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const clientId = localStorage.getItem('userId');
      const role = localStorage.getItem('role');
      
      // Get current week for API call
      const currentWeek = Math.ceil((new Date() - new Date(new Date().getFullYear(), 0, 1)) / (7 * 24 * 60 * 60 * 1000));
      
      const requestData = {
        user_id: clientId,
        user_type: role === 'M' ? 'master' : role === 'BR' ? 'broker' : role === 'A' ? 'admin' : 'user',
        week: currentWeek
      };
      
      const response = await fetch(`${config.API_BASE_URL}getDownOrders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Process orders similar to mtrades.php
        let orders_final = [];
        data.forEach(order => {
          let broker_brokerage = JSON.parse(order['broker_brokerage'] || '{}');
          let brokers = [];
          
          for (let key in broker_brokerage) {
            if (broker_brokerage.hasOwnProperty(key)) {
              brokers.push(key);
            }
          }
          
          let broker_str = brokers.join(",");
          order['brokers'] = broker_str;
          orders_final.push(order);
        });
        
        // Sort orders by orderDateTime (newest first)
        orders_final.sort((a, b) => {
          if (a.orderDateTime === b.orderDateTime) {
            return 0;
          }
          return (new Date(a.orderDateTime) > new Date(b.orderDateTime)) ? -1 : 1;
        });
        
        setOrders(orders_final);
        setTotalEntries(orders_final.length);
      } else {
        console.error('Failed to fetch orders');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch dropdown options
  const fetchDropdownOptions = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const clientId = localStorage.getItem('userId');
      const role = localStorage.getItem('role');
      
      // Fetch clients based on role
      let clientData = {
        user_id: clientId
      };
      
      if (role === 'BR') {
        clientData.user_type = 'broker';
      } else if (role === 'A') {
        clientData.client_type = 'admin';
      }
      
      const clientResponse = await fetch(`${config.API_BASE_URL}${role === 'BR' ? 'getDownClients' : 'getClientsOnly'}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(clientData)
      });
      
      if (clientResponse.ok) {
        const clientData = await clientResponse.json();
        setClients(clientData || []);
      }
      
      // Fetch segments for markets
      const segmentResponse = await fetch(`${config.API_BASE_URL}segments`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (segmentResponse.ok) {
        const segmentData = await segmentResponse.json();
        setMarkets(segmentData || []);
      }
      
      // Set order types
      setOrderTypes([
        { id: 1, name: 'Market' },
        { id: 2, name: 'Limit' },
        { id: 3, name: 'SL' }
      ]);
      
    } catch (error) {
      console.error('Error fetching dropdown options:', error);
    }
  };

  // Fetch scripts for selected segment
  const fetchScriptsOfSegment = async (segment) => {
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${config.API_BASE_URL}getScriptsOfSegment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          segment: segment || 'all'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setScripts(data || []);
      }
    } catch (error) {
      console.error('Error fetching scripts:', error);
    }
  };

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Fetch scripts when segment changes
    if (field === 'selectedSegment') {
      fetchScriptsOfSegment(value);
    }
  };

  // Search functions for dropdowns
  const handleScriptSearch = (value) => {
    if (value) {
      const filteredScripts = scripts.filter(script => 
        script.dispname.toLowerCase().includes(value.toLowerCase())
      );
      setScriptSearchResults(filteredScripts);
      setShowScriptResults(true);
    } else {
      setShowScriptResults(false);
    }
  };

  const handleClientSearch = (value) => {
    if (value) {
      const filteredClients = clients.filter(client => 
        client.clientName.toLowerCase().includes(value.toLowerCase()) ||
        client.clientId.toLowerCase().includes(value.toLowerCase())
      );
      setClientSearchResults(filteredClients);
      setShowClientResults(true);
    } else {
      setShowClientResults(false);
    }
  };

  const handleBrokerSearch = (value) => {
    if (value) {
      const filteredBrokers = clients.filter(client => 
        client.role === 'BR' && (
          client.clientName.toLowerCase().includes(value.toLowerCase()) ||
          client.clientId.toLowerCase().includes(value.toLowerCase())
        )
      );
      setBrokerSearchResults(filteredBrokers);
      setShowBrokerResults(true);
    } else {
      setShowBrokerResults(false);
    }
  };

  const selectScript = (script) => {
    setFilters(prev => ({
      ...prev,
      selectedStockName: script.dispname
    }));
    setShowScriptResults(false);
  };

  const selectClient = (client) => {
    setFilters(prev => ({
      ...prev,
      selectedClientId: client.clientId
    }));
    setShowClientResults(false);
  };

  const selectBroker = (broker) => {
    setFilters(prev => ({
      ...prev,
      selectedBrokerId: broker.clientId
    }));
    setShowBrokerResults(false);
  };

  // Clear all filters (matching mtrades.php)
  const clearFilters = () => {
    setFilters({
      selectedClientId: '',
      selectedStockName: '',
      selectedBrokerId: '',
      selectedSegment: '',
      selectedStatus: '3',
      clientIP: '',
      orderBroker: ''
    });
    setSearchQuery('');
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    fetchOrders();
  };

  // Export functionality (placeholder for future implementation)
  const handleExport = async () => {
    console.log('Export functionality not implemented yet');
  };

  // Filter orders based on current filters (exact match with mtrades.php)
  const getFilteredOrders = () => {
    if (!orders || orders.length === 0) return [];
    
    const filtered = orders.filter((order) => {
      // Filter by clientId
      const clientMatch = !filters.selectedClientId || order.clientId === filters.selectedClientId;
      
      // Filter by exchange
      const exchangeMatch = !filters.selectedSegment || order.exchange === filters.selectedSegment;
      
      // Filter by stockName (case-insensitive)
      const stockNameMatch = !filters.selectedStockName || 
        order.stockName.toLowerCase().includes(filters.selectedStockName.toLowerCase());

      // Filter by status (exact match with mtrades.php)
      let statusMatch;
      let statusMatch2;
      
      if(filters.selectedStatus == 3) {
        statusMatch = (!filters.selectedStatus || order.orderStatus == 3) && order.isDeleted == 0;
        statusMatch2 = (!filters.selectedStatus || order.orderStatus == 2) && order.isDeleted == 0;
      } else if(filters.selectedStatus == 5){
        statusMatch = order.isDeleted == 1;
      } else {
        statusMatch = (!filters.selectedStatus || order.orderStatus === filters.selectedStatus) && order.isDeleted == 0;
      }

      //Search by IP
      const ipMatch = !filters.clientIP || order.ip === filters.clientIP;

      // Broker match
      const brokerMatch = !filters.selectedBrokerId || order.brokers.includes(filters.selectedBrokerId);

      // Combine all filter conditions using logical AND
      return clientMatch && stockNameMatch && exchangeMatch && ipMatch && (statusMatch || statusMatch2) && brokerMatch;
    });

    return filtered;
  };

  // Helper functions
  const getOrderStatusColor = (status) => {
    switch(status) {
      case 1: return '#ff9800'; // Pending
      case 2: 
      case 3: return '#4caf50'; // Executed
      case 4: return '#f44336'; // Cancelled
      default: return '#666666';
    }
  };

  const getBuySellColor = (buySell) => {
    return buySell === 1 ? '#4caf50' : '#f44336';
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime || dateTime === '0000-00-00 00:00:00') return '--:--:--';
    return new Date(dateTime).toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getOrderTypeName = (orderType) => {
    const orderTypes = ["NA", "Market", "Limit", "SL"];
    return orderTypes[orderType] || 'Unknown';
  };

  const getCloseTypeName = (closeType) => {
    const closeTypes = ["NA", "Auto Exit", "Contract Expired", "Delivery Square-Up", "unknown"];
    return closeTypes[closeType] || 'Unknown';
  };

  // Effects
  useEffect(() => {
    fetchDropdownOptions();
    fetchOrders();
  }, []);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
  }, [filters]);

  return (
    <Box className={`master-trades ${isDarkMode ? 'dark-theme' : 'light-theme'}`} data-theme={isDarkMode ? 'dark' : 'light'}>
      {/* Desktop Side Panel + Main Content */}
      <Box className="master-desktop-layout">
        {/* Desktop Side Panel Filters - Toggleable */}
        {showFilters && (
          <Box className="master-side-panel">
            <Card className="master-side-panel-card">
              <CardContent className="master-side-panel-content">
                <Typography variant="h6" className="master-side-panel-title">
                  Filters
                </Typography>
                
                <Box className="master-side-panel-filters">
                  <FormControl fullWidth size="small" className="master-side-panel-field">
                    <InputLabel>Client</InputLabel>
                    <Select
                      value={filters.selectedClientId}
                      onChange={(e) => handleFilterChange('selectedClientId', e.target.value)}
                      label="Client"
                    >
                      <MenuItem value="">All Clients</MenuItem>
                      {clients.map((client) => (
                        <MenuItem key={client.clientId || client.id} value={client.clientId || client.id}>
                          {client.clientName || client.name} ({client.clientId || client.id})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth size="small" className="master-side-panel-field">
                    <InputLabel>Segment</InputLabel>
                    <Select
                      value={filters.selectedSegment}
                      onChange={(e) => handleFilterChange('selectedSegment', e.target.value)}
                      label="Segment"
                    >
                      <MenuItem value="">All Segments</MenuItem>
                      {markets.map((market) => (
                        <MenuItem key={market.segment} value={market.segment}>
                          {market.segment}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth size="small" className="master-side-panel-field">
                    <InputLabel>Stock</InputLabel>
                    <Select
                      value={filters.selectedStockName}
                      onChange={(e) => handleFilterChange('selectedStockName', e.target.value)}
                      label="Stock"
                    >
                      <MenuItem value="">All Stocks</MenuItem>
                      {scripts.map((script) => (
                        <MenuItem key={script.instrumentIdentifier || script.id} value={script.dispname || script.name}>
                          {script.dispname || script.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth size="small" className="master-side-panel-field">
                    <InputLabel>Broker</InputLabel>
                    <Select
                      value={filters.selectedBrokerId}
                      onChange={(e) => handleFilterChange('selectedBrokerId', e.target.value)}
                      label="Broker"
                    >
                      <MenuItem value="">All Brokers</MenuItem>
                      {brokers.map((broker) => (
                        <MenuItem key={broker.brokerId || broker.id} value={broker.brokerId || broker.id}>
                          {broker.brokerName || broker.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth size="small" className="master-side-panel-field">
                    <InputLabel>Order Type</InputLabel>
                    <Select
                      value={filters.selectedOrderType}
                      onChange={(e) => handleFilterChange('selectedOrderType', e.target.value)}
                      label="Order Type"
                    >
                      <MenuItem value="">All Order Types</MenuItem>
                      {orderTypes.map((type) => (
                        <MenuItem key={type.orderType} value={type.orderType}>
                          {type.orderTypeName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth size="small" className="master-side-panel-field">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.selectedStatus}
                      onChange={(e) => handleFilterChange('selectedStatus', e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="">All Status</MenuItem>
                      <MenuItem value="open">Open</MenuItem>
                      <MenuItem value="closed">Closed</MenuItem>
                      <MenuItem value="partial">Partial</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <TextField
                    fullWidth
                    size="small"
                    label="Date"
                    type="date"
                    value={filters.selectedDate}
                    onChange={(e) => handleFilterChange('selectedDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    className="master-side-panel-field"
                  />
                  
                  <FormControl fullWidth size="small" className="master-side-panel-field">
                    <InputLabel>Time Range</InputLabel>
                    <Select
                      value={filters.selectedTimeRange}
                      onChange={(e) => handleFilterChange('selectedTimeRange', e.target.value)}
                      label="Time Range"
                    >
                      <MenuItem value="today">Today</MenuItem>
                      <MenuItem value="yesterday">Yesterday</MenuItem>
                      <MenuItem value="week">This Week</MenuItem>
                      <MenuItem value="month">This Month</MenuItem>
                      <MenuItem value="custom">Custom Range</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Clear />}
                    onClick={clearFilters}
                    className="master-side-panel-clear-btn"
                    fullWidth
                  >
                    Clear Filters
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Main Content */}
        <Box className="master-main-content">
      {/* Header */}
      <Card className="master-header-card">
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h4" className="master-page-title">
              Master Trades
            </Typography>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => setShowFilters(!showFilters)}
                className="filter-toggle-btn"
              >
                Filters
              </Button>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={handleRefresh}
                disabled={loading}
                className="refresh-btn"
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<FileDownload />}
                onClick={handleExport}
                className="export-btn"
              >
                Export
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Filters Section */}
      {showFilters && (
      <Card className={`master-trades-filters ${isDarkMode ? 'dark-theme' : 'light-theme'}`}>
        <CardContent className="master-trades-filters-content">
          {/* <Typography variant="h6" className="master-trades-title">
            Master Trades
          </Typography> */}
          
          {/* Compact Filters Row */}
          <Grid container spacing={1} className="master-trades-compact-filters">
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.selectedStatus}
                  onChange={(e) => handleFilterChange('selectedStatus', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="1">Pending</MenuItem>
                  <MenuItem value="3">Executed</MenuItem>
                  <MenuItem value="4">Cancelled</MenuItem>
                  <MenuItem value="5">Deleted</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Market</InputLabel>
                <Select
                  value={filters.selectedSegment}
                  onChange={(e) => handleFilterChange('selectedSegment', e.target.value)}
                  label="Market"
                >
                  <MenuItem value="">All</MenuItem>
                  {markets.map((market) => (
                    <MenuItem key={market.segment} value={market.segment}>
                      {market.segment}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <div className="master-trades-searchable-field">
                <TextField
                  fullWidth
                  size="small"
                  label="Script"
                  placeholder="Search Script"
                  value={filters.selectedStockName}
                  onChange={(e) => {
                    handleFilterChange('selectedStockName', e.target.value);
                    handleScriptSearch(e.target.value);
                  }}
                  onFocus={() => {
                    if (filters.selectedStockName) {
                      handleScriptSearch(filters.selectedStockName);
                    }
                  }}
                />
                {showScriptResults && scriptSearchResults.length > 0 && (
                  <div className="master-trades-search-results">
                    {scriptSearchResults.map((script, index) => (
                      <div
                        key={index}
                        className="master-trades-search-item"
                        onClick={() => selectScript(script)}
                      >
                        {script.dispname}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <div className="master-trades-searchable-field">
                <TextField
                  fullWidth
                  size="small"
                  label="Client"
                  placeholder="Search Client"
                  value={filters.selectedClientId}
                  onChange={(e) => {
                    handleFilterChange('selectedClientId', e.target.value);
                    handleClientSearch(e.target.value);
                  }}
                  onFocus={() => {
                    if (filters.selectedClientId) {
                      handleClientSearch(filters.selectedClientId);
                    }
                  }}
                />
                {showClientResults && clientSearchResults.length > 0 && (
                  <div className="master-trades-search-results">
                    {clientSearchResults.map((client, index) => (
                      <div
                        key={index}
                        className="master-trades-search-item"
                        onClick={() => selectClient(client)}
                      >
                        {client.clientName} ({client.clientId})
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <div className="master-trades-searchable-field">
                <TextField
                  fullWidth
                  size="small"
                  label="Broker"
                  placeholder="Search Broker"
                  value={filters.selectedBrokerId}
                  onChange={(e) => {
                    handleFilterChange('selectedBrokerId', e.target.value);
                    handleBrokerSearch(e.target.value);
                  }}
                  onFocus={() => {
                    if (filters.selectedBrokerId) {
                      handleBrokerSearch(filters.selectedBrokerId);
                    }
                  }}
                />
                {showBrokerResults && brokerSearchResults.length > 0 && (
                  <div className="master-trades-search-results">
                    {brokerSearchResults.map((broker, index) => (
                      <div
                        key={index}
                        className="master-trades-search-item"
                        onClick={() => selectBroker(broker)}
                      >
                        {broker.clientName} ({broker.clientId})
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="IP"
                placeholder="Search IP"
                value={filters.clientIP}
                onChange={(e) => handleFilterChange('clientIP', e.target.value)}
              />
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box className="master-trades-actions">
            <Button
              variant="outlined"
              size="small"
              className="master-trades-clear-btn"
              onClick={clearFilters}
              startIcon={<Clear />}
            >
              Clear
            </Button>
          </Box>
        </CardContent>
      </Card>
      )}

      {/* Orders Table Section */}
      <Card className={`master-trades-table-card ${isDarkMode ? 'dark-theme' : 'light-theme'}`}>
        <CardContent className="master-trades-table-content">
          {/* Compact Table Header */}
          <Box className="master-trades-table-header">
            <Typography variant="body2" className="master-trades-entries-info">
              {getFilteredOrders().length} entries
            </Typography>
            <FormControl size="small">
              <Select
                value={entriesPerPage}
                onChange={(e) => setEntriesPerPage(e.target.value)}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Orders Table */}
          <TableContainer component={Paper} className="master-trades-table-container">
            <Table className="master-trades-table">
              <TableHead>
                <TableRow className="master-trades-table-header-row">
                  <TableCell className="master-trades-table-header-cell">#</TableCell>
                  <TableCell className="master-trades-table-header-cell">CLIENT</TableCell>
                  <TableCell className="master-trades-table-header-cell">SCRIPT</TableCell>
                  <TableCell className="master-trades-table-header-cell">BROKERS</TableCell>
                  <TableCell className="master-trades-table-header-cell">BUY/SELL</TableCell>
                  <TableCell className="master-trades-table-header-cell">LOTS</TableCell>
                  <TableCell className="master-trades-table-header-cell">QTY</TableCell>
                  <TableCell className="master-trades-table-header-cell">PRICE</TableCell>
                  <TableCell className="master-trades-table-header-cell">STATUS</TableCell>
                  <TableCell className="master-trades-table-header-cell">ORDER TIME</TableCell>
                  <TableCell className="master-trades-table-header-cell">EXECUTION</TableCell>
                  <TableCell className="master-trades-table-header-cell">IP</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredOrders()
                  .slice((currentPage - 1) * entriesPerPage, currentPage * entriesPerPage)
                  .map((order, index) => (
                  <TableRow 
                    key={order.orderId} 
                    className={`master-trades-table-row ${
                      order.buy_sell === 1 ? 'buy-class' : 'sell-class'
                    }`}
                  >
                    <TableCell className="master-trades-table-cell">
                      {index + 1}
                    </TableCell>
                    <TableCell className="master-trades-table-cell">
                      {order.clientName}({order.clientId})
                    </TableCell>
                    <TableCell className="master-trades-table-cell">
                      <div>
                        <div style={{ fontWeight: 600 }}>
                          {order.stockName}
                          {order.isManualTrade === 1 && (
                            <span style={{
                              backgroundColor: 'red',
                              color: 'white',
                              borderRadius: '4px',
                              padding: '0px 4px 0px 6px',
                              fontSize: '12px',
                              marginLeft: '5px'
                            }}>
                              A
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '11px' }}>
                          {order.stockExpiry}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="master-trades-table-cell">
                      {order.brokers}
                    </TableCell>
                    <TableCell className="master-trades-table-cell">
                      <div>
                        <div style={{ 
                          color: order.buy_sell === 1 ? '#25b825' : 'red',
                          fontWeight: 600
                        }}>
                          {order.buy_sell === 1 ? 'Buy' : 'Sell'}
                        </div>
                        <div style={{ fontSize: '11px', fontWeight: 600 }}>
                          {order.closeType === 0 ? (
                            <>
                              {order.orderTransactionType === 1 ? 'Entry' : 'Exit'} - {getOrderTypeName(order.orderType)}
                            </>
                          ) : (
                            <>
                              {order.orderTransactionType === 1 ? 'Entry' : 'Exit'} - {getCloseTypeName(order.closeType)}
                            </>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="master-trades-table-cell">
                      {Number(order.orderLotQuantity).toFixed(2)}
                    </TableCell>
                    <TableCell className="master-trades-table-cell">
                      {Number(order.orderQuantity).toFixed(2)}
                    </TableCell>
                    <TableCell className="master-trades-table-cell">
                      ₹{Number(order.orderPrice).toFixed(2)}
                    </TableCell>
                    <TableCell className="master-trades-table-cell">
                      {order.isDeleted === 1 ? (
                        <span style={{ color: '#f44336' }}>Deleted</span>
                      ) : (
                        <>
                          {order.orderStatus === 1 && <span style={{ color: '#ff9800' }}>Pending</span>}
                          {order.orderStatus === 4 && <span style={{ color: '#f44336' }}>Cancelled</span>}
                          {(order.orderStatus === 2 || order.orderStatus === 3) && <span style={{ color: '#4caf50' }}>Executed</span>}
                        </>
                      )}
                    </TableCell>
                    <TableCell className="master-trades-table-cell">
                      {order.orderDateTime}
                    </TableCell>
                    <TableCell className="master-trades-table-cell">
                      {order.orderStatus === 4 ? order.canDateTime : order.exeDateTime}
                    </TableCell>
                    <TableCell className="master-trades-table-cell">
                      {order.ip}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box className="master-trades-pagination">
            <Typography variant="body2" className="master-trades-pagination-info">
              Showing {((currentPage - 1) * entriesPerPage) + 1} to {Math.min(currentPage * entriesPerPage, getFilteredOrders().length)} of {getFilteredOrders().length} entries
            </Typography>
            <Pagination
              count={Math.ceil(getFilteredOrders().length / entriesPerPage)}
              page={currentPage}
              onChange={(event, page) => setCurrentPage(page)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        </CardContent>
      </Card>
        </Box>
      </Box>

    </Box>
  );
};

export default MasterTrades;
